import { z } from 'zod';
import { NIVELES, PERMISOS, TIPOS_AUTORIZACION_SOLICITABLES, type Nivel, type Permiso, type TipoAutorizacion } from '../permisos.ts';
import { decimalNoNegativo, email, paginacion, uuid } from './comun.ts';

/** Lo que se puede pedir hoy (sin «Retiro parcial de caja», que ninguna operación pide: hallazgo E9). */
const tipoSolicitable = z.enum(TIPOS_AUTORIZACION_SOLICITABLES as [TipoAutorizacion, ...TipoAutorizacion[]]);
const motivo = z.string().trim().min(3, 'Indica el motivo').max(300);
/** Lo que se pide autorizar (cliente, documento, porcentaje…): texto corto por clave, para mostrarlo a quien aprueba. */
const detalle = z.record(z.string().max(40), z.union([z.string().max(300), z.number(), z.boolean(), z.null()])).optional();

/** Solicitud a distancia: queda pendiente hasta que alguien con el permiso la resuelva. */
export const autorizacionSolicitarInput = z.object({
  tipo: tipoSolicitable,
  motivo,
  detalle,
  monto_usd: decimalNoNegativo.optional(),
});
export type AutorizacionSolicitarInput = z.infer<typeof autorizacionSolicitarInput>;

/** Autorización con clave: quien tiene el permiso escribe su correo y contraseña en la pantalla del solicitante. */
export const autorizacionClaveInput = autorizacionSolicitarInput.extend({
  email,
  password: z.string().min(1, 'La contraseña es obligatoria').max(200),
});
export type AutorizacionClaveInput = z.infer<typeof autorizacionClaveInput>;

export const autorizacionResolverInput = z.object({
  decision: z.enum(['aprobada', 'negada']),
  comentario: z.string().trim().max(300).optional(),
});
export type AutorizacionResolverInput = z.infer<typeof autorizacionResolverInput>;

export const autorizacionesFiltros = paginacion.extend({
  estado: z.enum(['pendiente', 'aprobada', 'negada', 'usada', 'vencida']).optional(),
  /** `mias` = las que pedí yo; `resolver` = las que puedo resolver (default). */
  ver: z.enum(['resolver', 'mias']).default('resolver'),
  desde: z.iso.date().optional(),
  hasta: z.iso.date().optional(),
});
export type AutorizacionesFiltros = z.infer<typeof autorizacionesFiltros>;

/** Autorizaciones que acompañan una operación (una venta puede necesitar varias: descuento + cliente vencido…). */
export const autorizacionesAdjuntas = z.array(uuid).max(6).default([]);

export interface AutorizacionDTO {
  id: string;
  sucursal_id: string;
  sucursal_nombre: string;
  tipo: TipoAutorizacion;
  tipo_etiqueta: string;
  via: 'clave' | 'remota';
  estado: 'pendiente' | 'aprobada' | 'negada' | 'usada' | 'vencida';
  solicitante_nombre: string;
  motivo: string;
  detalle: Record<string, string | number | boolean | null> | null;
  monto_usd: string | null;
  resuelta_por_nombre: string | null;
  resuelta_en: string | null;
  comentario: string | null;
  caduca_en: string;
  usada_en: string | null;
  creado_en: string;
  /** El usuario que consulta puede aprobarla o negarla. */
  puedo_resolver: boolean;
}

// ─── Permisos por usuario ───────────────────────────────────────────────────
export const permisosUsuarioInput = z.object({
  /** Solo las diferencias con la plantilla del rol: nivel null = quitar el permiso. */
  permisos: z
    .array(z.object({ permiso: z.enum(PERMISOS as [Permiso, ...Permiso[]]), nivel: z.enum(NIVELES).nullable() }))
    .max(PERMISOS.length),
});
export type PermisosUsuarioInput = z.infer<typeof permisosUsuarioInput>;

export interface PermisosUsuarioDTO {
  usuario_id: string;
  nombre: string;
  rol: string;
  /** Una fila por permiso del sistema: lo que da la plantilla del rol, lo propio del usuario y el resultado. */
  filas: { permiso: Permiso; etiqueta: string; plantilla: Nivel | null; propio: Nivel | null | undefined; efectivo: Nivel | null }[];
}
